import React, { useState } from "react";
import { api } from "../../lib/api";
import { toast } from "sonner";
import { Input } from "../ui/input";
import { DownloadSimple, Plus, Trash, MagnifyingGlass, UsersThree, TestTube, PaperPlaneTilt } from "@phosphor-icons/react";

const ASSOCIATIONS = [
  "Auckland", "Bay of Plenty", "Canterbury", "Counties Manukau", "Hawkes Bay",
  "Manawatu", "Marlborough", "Nelson", "North Shore", "North Taranaki",
  "Northland", "Otago", "South Canterbury", "Southland", "Waikato",
  "Waitemata", "Whanganui", "Wellington",
];
const EVENTS = ["Open Men", "Open Women", "U13 Boys", "U13 Girls", "U15 Boys", "U15 Girls", "U19 Boys", "U19 Girls"];

const SAMPLE_TEAMS = [
  { event: "Open Men", rank: 1, players: [
    { name: "Hyrum Sunnex", imr: "5704", dob: "1978-09-05", gender: "M", assn_code: "WT" },
    { name: "Andrew Leonard", imr: "9716", dob: "1978-03-07", gender: "M", assn_code: "WK" },
    { name: "Reagan Gumilet", imr: "", dob: "", gender: "M", assn_code: "TM" },
    { name: "Paul Kyle", imr: "8167", dob: "1963-01-19", gender: "M", assn_code: "BP" },
  ] },
  { event: "Open Women", rank: 1, players: [
    { name: "Barb Shepp", imr: "36146", dob: "1974-08-09", gender: "F", assn_code: "WT" },
    { name: "Mimi Walle", imr: "6624", dob: "1966-01-07", gender: "F", assn_code: "C" },
    { name: "Julie Adamson", imr: "15742", dob: "1975-07-09", gender: "F", assn_code: "BP" },
  ] },
];

const emptyTeam = () => ({ event: "Open Men", rank: 1, players: [] });
const fieldCls = "w-full rounded-none border border-navy/20 bg-white h-10 px-3 text-sm";

export const TeamEntryPanel = () => {
  const [association, setAssociation] = useState("Bay of Plenty");
  const [accept, setAccept] = useState(true);
  const [manager, setManager] = useState({ name: "Paul Kyle", email: "pk@chartwell.co.nz", phone: "+64 272422757" });
  const [teams, setTeams] = useState(SAMPLE_TEAMS);
  const [generating, setGenerating] = useState(false);
  const [emailTo, setEmailTo] = useState("");
  const [emailing, setEmailing] = useState(false);

  // per-team player search
  const [searchIdx, setSearchIdx] = useState(null);
  const [query, setQuery] = useState("");
  const [results, setResults] = useState([]);
  const [searching, setSearching] = useState(false);

  const setTeam = (i, patch) => setTeams(teams.map((t, idx) => idx === i ? { ...t, ...patch } : t));
  const addTeam = () => setTeams([...teams, emptyTeam()]);
  const removeTeam = (i) => setTeams(teams.filter((_, idx) => idx !== i));

  const doSearch = async (i, q) => {
    setSearchIdx(i); setQuery(q);
    if (q.trim().length < 2) { setResults([]); return; }
    setSearching(true);
    try {
      const { data } = await api.get(`/admin/players?q=${encodeURIComponent(q)}&limit=8`);
      setResults(data.items);
    } catch (e) { /* ignore */ } finally { setSearching(false); }
  };

  const addPlayer = (i, p) => {
    const t = teams[i];
    if (t.players.length >= 5) { toast.error("Max 5 players per team"); return; }
    if (t.players.some((x) => x.name === p.name && x.imr === p.imr)) { toast.error("Player already added"); return; }
    setTeam(i, { players: [...t.players, { name: p.name, imr: p.imr || "", dob: p.dob || "", gender: p.gender || "", assn_code: p.assn_code || "" }] });
    setQuery(""); setResults([]); setSearchIdx(null);
  };
  const removePlayer = (i, pi) => setTeam(i, { players: teams[i].players.filter((_, idx) => idx !== pi) });

  const loadSample = () => {
    setAssociation("Bay of Plenty"); setAccept(true);
    setManager({ name: "Paul Kyle", email: "pk@chartwell.co.nz", phone: "+64 272422757" });
    setTeams(SAMPLE_TEAMS);
    toast.success("Loaded the Bay of Plenty test sample");
  };

  const generate = async () => {
    if (!association) { toast.error("Select an association"); return; }
    if (!teams.some((t) => t.players.length > 0)) { toast.error("Add at least one player"); return; }
    setGenerating(true);
    try {
      const res = await api.post("/admin/team-entry/generate",
        { association, accept_conditions: accept, manager, teams }, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data]));
      const a = document.createElement("a");
      a.href = url; a.download = "2026_NZ_Open_Teams_Entry.xlsx";
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Entry form generated — check your downloads");
    } catch (e) { toast.error("Could not generate the entry form"); }
    finally { setGenerating(false); }
  };

  const emailCopy = async () => {
    if (!association) { toast.error("Select an association"); return; }
    if (!emailTo.trim() || !emailTo.includes("@")) { toast.error("Enter a valid recipient email"); return; }
    if (!teams.some((t) => t.players.length > 0)) { toast.error("Add at least one player"); return; }
    setEmailing(true);
    try {
      await api.post("/admin/team-entry/email", {
        association, accept_conditions: accept, manager, teams,
        to_email: emailTo.trim(), public_base: process.env.REACT_APP_BACKEND_URL,
      });
      toast.success(`Entry form emailed to ${emailTo.trim()}`);
    } catch (e) { toast.error(e?.response?.data?.detail || "Could not send the email"); }
    finally { setEmailing(false); }
  };

  return (
    <div className="space-y-6" data-testid="team-entry-panel">
      <div className="bg-white border border-navy/10 p-6">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h3 className="font-heading text-xl text-navy mb-1 flex items-center gap-2"><UsersThree size={22} /> NZ Open Team Entry</h3>
            <p className="text-sm text-navy/60 max-w-2xl">Build teams from the player database, then download the official 2026 NZ Open Team Championships entry form — pre-filled and ready to email to TTNZ. Player IMR, DOB and gender auto-fill in Excel from the registered name.</p>
          </div>
          <button onClick={loadSample} data-testid="team-load-sample" className="inline-flex items-center gap-2 text-navy border border-navy/20 px-4 py-2 rounded-sm text-sm font-semibold hover:border-gold">
            <TestTube size={16} /> Load test sample
          </button>
        </div>

        <div className="grid sm:grid-cols-2 gap-4 mt-5">
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Association</label>
            <select value={association} onChange={(e) => setAssociation(e.target.value)} className={fieldCls} data-testid="team-association">
              {ASSOCIATIONS.map((a) => <option key={a} value={a}>{a}</option>)}
            </select>
          </div>
          <label className="flex items-end gap-2 pb-2">
            <input type="checkbox" checked={accept} onChange={(e) => setAccept(e.target.checked)} data-testid="team-accept" className="h-4 w-4 accent-gold" />
            <span className="text-sm text-navy">Accept entry conditions on behalf of listed players</span>
          </label>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Manager name</label>
            <Input value={manager.name} onChange={(e) => setManager({ ...manager, name: e.target.value })} className={fieldCls} data-testid="team-mgr-name" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Manager email</label>
            <Input value={manager.email} onChange={(e) => setManager({ ...manager, email: e.target.value })} className={fieldCls} data-testid="team-mgr-email" />
          </div>
          <div>
            <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Manager phone</label>
            <Input value={manager.phone} onChange={(e) => setManager({ ...manager, phone: e.target.value })} className={fieldCls} data-testid="team-mgr-phone" />
          </div>
        </div>
      </div>

      {teams.map((team, i) => (
        <div key={i} className="bg-white border border-navy/10 p-6" data-testid="team-card">
          <div className="flex items-center justify-between mb-4">
            <h4 className="font-heading text-lg text-navy">Team {i + 1}</h4>
            {teams.length > 1 && (
              <button onClick={() => removeTeam(i)} data-testid="team-remove" className="text-navy/40 hover:text-red-500 inline-flex items-center gap-1 text-sm"><Trash size={16} /> Remove team</button>
            )}
          </div>
          <div className="grid sm:grid-cols-2 gap-4 mb-4">
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Event</label>
              <select value={team.event} onChange={(e) => setTeam(i, { event: e.target.value })} className={fieldCls} data-testid="team-event">
                {EVENTS.map((ev) => <option key={ev} value={ev}>{ev}</option>)}
              </select>
            </div>
            <div>
              <label className="text-xs font-semibold uppercase tracking-wide text-navy/60">Seeding rank (1-10)</label>
              <select value={team.rank} onChange={(e) => setTeam(i, { rank: Number(e.target.value) })} className={fieldCls} data-testid="team-rank">
                {Array.from({ length: 10 }, (_, n) => n + 1).map((n) => <option key={n} value={n}>{n}</option>)}
              </select>
            </div>
          </div>

          <div className="space-y-2 mb-4">
            {team.players.map((p, pi) => (
              <div key={pi} className="flex items-center justify-between bg-navy/5 px-3 py-2 rounded-sm" data-testid="team-player-row">
                <span className="text-sm text-navy"><strong>{p.name}</strong>
                  <span className="text-navy/50 ml-2 text-xs">IMR {p.imr || "pending"} · {p.gender || "?"} · {p.assn_code || "?"}</span>
                </span>
                <button onClick={() => removePlayer(i, pi)} data-testid="team-player-remove" className="text-navy/40 hover:text-red-500"><Trash size={16} /></button>
              </div>
            ))}
            {team.players.length === 0 && <p className="text-sm text-navy/40">No players yet — search below to add (minimum 3).</p>}
            <p className="text-xs text-navy/40">{team.players.length}/5 players{team.players.length < 3 ? " — at least 3 required" : ""}</p>
          </div>

          <div className="relative">
            <div className="flex items-center gap-2 border border-navy/20 px-3 rounded-sm">
              <MagnifyingGlass size={18} className="text-navy/40" />
              <input value={searchIdx === i ? query : ""} onChange={(e) => doSearch(i, e.target.value)} onFocus={() => { setSearchIdx(i); setQuery(""); setResults([]); }}
                placeholder="Search players by name or IMR…" className="w-full h-10 text-sm outline-none" data-testid="team-player-search" />
            </div>
            {searchIdx === i && (query.trim().length >= 2) && (
              <div className="absolute z-10 left-0 right-0 bg-white border border-navy/15 shadow-lg max-h-64 overflow-y-auto" data-testid="team-search-results">
                {searching && <div className="px-3 py-2 text-sm text-navy/50">Searching…</div>}
                {!searching && results.length === 0 && <div className="px-3 py-2 text-sm text-navy/50">No matches</div>}
                {results.map((p) => (
                  <button key={p.id} onClick={() => addPlayer(i, p)} data-testid="team-search-result"
                    className="w-full text-left px-3 py-2 text-sm hover:bg-gold/10 border-b border-navy/5 flex justify-between">
                    <span className="text-navy">{p.name}</span>
                    <span className="text-navy/40 text-xs">IMR {p.imr || "—"} · {p.assn_code || "?"}</span>
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ))}

      <div className="flex flex-wrap items-center gap-3">
        <button onClick={addTeam} data-testid="team-add" className="inline-flex items-center gap-2 text-navy border border-navy/20 px-5 py-2.5 rounded-sm font-semibold text-sm hover:border-gold">
          <Plus size={18} weight="bold" /> Add another team
        </button>
        <button onClick={generate} disabled={generating} data-testid="team-generate" className="inline-flex items-center gap-2 bg-gold text-navy font-semibold px-6 py-2.5 rounded-sm hover:bg-navy hover:text-white transition-colors disabled:opacity-40">
          <DownloadSimple size={18} /> {generating ? "Generating…" : "Generate & download Excel"}
        </button>
      </div>

      <div className="bg-white border border-navy/10 p-6">
        <h4 className="font-heading text-lg text-navy mb-1 flex items-center gap-2"><PaperPlaneTilt size={18} /> Email a copy</h4>
        <p className="text-sm text-navy/60 mb-4">Send a copy of this completed entry form (as a secure download link) to any address — e.g. TTNZ entries or an association contact.</p>
        <div className="flex flex-wrap items-center gap-3">
          <Input type="email" value={emailTo} onChange={(e) => setEmailTo(e.target.value)} placeholder="recipient@example.com" className="max-w-xs rounded-none border-navy/20" data-testid="team-email-to" />
          <button onClick={emailCopy} disabled={emailing} data-testid="team-email-send" className="inline-flex items-center gap-2 text-navy border border-navy/20 px-5 py-2.5 rounded-sm font-semibold text-sm hover:bg-navy hover:text-white transition-colors disabled:opacity-40">
            <PaperPlaneTilt size={16} /> {emailing ? "Sending…" : "Email a copy"}
          </button>
        </div>
      </div>
    </div>
  );
};
